<template>
  <span
    :class="{
      'locale-item-fi': true,
      'locale-item-fi--has-multi-languages': multiLanguages,
      'locale-item-fi--active': multiLanguages && locale.code === currentCode,
    }"
  >
    <component
      :is="multiLanguages && isNotCurrentLocale ? 'a' : 'span'"
      :href="href"
      class="locale-item-fi__name"
    >
      {{ locale.language }}
    </component>
  </span>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { computed, ref, watch, watchEffect } from 'vue';

import { useCookies } from '@/composables/cookies';
import type { Locale } from '@/composables/countrySelect';
import { useFischer } from '@/composables/fischerPlugin';
import { useSlugLink } from '@/composables/slugLink';
import type {
  GetCategorySlugByIdQuery,
  GetCategorySlugByIdQueryVariables,
  GetProductSlugByIdQuery,
  GetProductSlugByIdQueryVariables,
} from '@/generated/graphql';
import GET_CATEGORY_SLUG_BY_ID from '@/graphql/getCategorySlugById.gql';
import GET_PRODUCT_SLUG_BY_ID from '@/graphql/getProductSlugById.gql';
import loadLayoutData from '@/loadLayoutData';

const props = defineProps<{
  locale: Locale;
  currentCode: string;
  multiLanguages: boolean;
}>();

const { getSlugLink } = useSlugLink();
const { cookies } = useCookies();
const fischer = useFischer();

const toHref = async (slug: string | null, layoutData: null | Promise<any>) => {
  if (!slug) {
    return null;
  }

  try {
    const productsUrl = (await layoutData)?.sitecore.settings.site.productsPage.Url;
    return getSlugLink({ Slug: slug }, productsUrl);
  } catch (error) {
    console.error('Could not get item slug', error);
    return null;
  }
};

const apiLang = computed(() => {
  if (fischer.app.isFite() && props.locale.code === 'en') {
    return 'en_GB';
  }
  return props.locale.code.replace(/-/, '_');
});

const getLayoutDataPromise = () => {
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  return loadLayoutData(baseUrl, fischer.pageId(), props.locale.code, cookies);
};

const isCategory = fischer.isCategoryPage() || fischer.isFamilyPage() || fischer.isSeriesPage();
const isProduct = fischer.isProductsPage();
const isNotCurrentLocale = computed<boolean>(() => props.locale.code !== props.currentCode);
const layoutData = computed(() =>
  isNotCurrentLocale.value && (isCategory || isProduct) ? getLayoutDataPromise() : null,
);

const href = ref<string | null>(null);

watchEffect(() => {
  if (layoutData.value === null) {
    href.value = props.locale.url;
  }
});

const { data: productData, error: productError } = useQuery<
  GetProductSlugByIdQuery,
  GetProductSlugByIdQueryVariables
>({
  query: GET_PRODUCT_SLUG_BY_ID,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    productId: fischer.catalogId() || '',
    apiLang: apiLang.value,
  })),
  pause: computed(() => IS_SSR || !isProduct || layoutData.value === null),
  requestPolicy: 'network-only',
});

watch(productError, (e) => {
  if (e) console.error('Could not get layout for country-selection', e);
});

watch(productData, async (data) => {
  if (data) {
    const slug = data.getCatalogProductDataById?.Slug;
    href.value = await toHref(slug, layoutData.value);
  }
});

const { data: categoryData, error: categoryError } = useQuery<
  GetCategorySlugByIdQuery,
  GetCategorySlugByIdQueryVariables
>({
  query: GET_CATEGORY_SLUG_BY_ID,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    categoryId: fischer.catalogId() || '',
    apiLang: apiLang.value,
  })),
  pause: computed(() => IS_SSR || !isCategory || layoutData.value === null),
  requestPolicy: 'network-only',
});

watch(categoryData, async (data) => {
  if (data) {
    const slug = data.getCatalogCategoryDataById?.Slug;
    href.value = await toHref(slug, layoutData.value);
  }
});

watch(categoryError, (e) => {
  if (e) console.error('Could not get category slug for country selection', e);
});
</script>

<style scoped src="./locale-item-fi" />
